/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, filter, withAssign, withInit } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { NumberUtil } from '@root/shared/core'
import { NumericFormat } from '@root/shared/core/format'

import type { Equivalence } from '@blade/std-lib'
import type { Action, Atom, AtomMut, Ctx } from '@reatom/framework'

export type ReatomNumericInput = Atom<BigNumber> & {
  raw: AtomMut<string>
  setRaw: Action<[raw: string], void>
}

export type ReatomNumericInputOptional = Atom<BigNumber | undefined> & {
  raw: AtomMut<string>
  setRaw: Action<[raw: string], void>
}

export type ReatomNumericInputArgs<Optional extends boolean> = {
  initialRawAmount?: string
  initialAmount?: BigNumber.Value | ((ctx: Ctx) => BigNumber.Value | undefined)
  /**
   * If true, the value will be undefined if the raw value is empty or invalid
   */
  optional?: Optional
}
export type ReatomNumericInputReturnType<Optional extends boolean> =
  Optional extends true ? ReatomNumericInputOptional : ReatomNumericInput

export const reatomNumericInput = <Optional extends boolean = false>(
  args: ReatomNumericInputArgs<Optional>,
  _name?: string,
): ReatomNumericInputReturnType<Optional> => {
  const amountRawAtom = atom('').pipe(
    withInit((ctx) => {
      if (args.initialRawAmount !== undefined) {
        return args.initialRawAmount
      }
      const initialAmount =
        typeof args.initialAmount === 'function'
          ? args.initialAmount(ctx)
          : args.initialAmount

      if (initialAmount) {
        return NumericFormat.formatPreset(initialAmount, 'full')
      }

      return ''
    }),
  )
  const setAmountRawAction = action((ctx, raw: string) => {
    amountRawAtom(ctx, raw)
  })

  const amountAtom = atom((ctx) => {
    const raw = ctx.spy(amountRawAtom)
    const parsed = raw ? NumberUtil.parseBigNumber(raw) : undefined
    if (args.optional) {
      return parsed
    }
    return parsed ?? BigNumber(0)
  }).pipe(filter((ctx, left, right) => !eqBigNumber(left, right)))

  return amountAtom.pipe(
    withAssign(() => ({
      raw: amountRawAtom,
      setRaw: setAmountRawAction,
    })),
  ) as ReatomNumericInputReturnType<Optional>
}

const eqBigNumber: Equivalence.Equivalence<BigNumber | undefined> = (
  left,
  right,
) => {
  if (left !== undefined && right !== undefined) {
    return left.eq(right)
  }
  return left === right
}
